// Patch a manual-match FRAME DELTA into the frames the client already holds.
//
// A manual match moves figures but touches only the rows it pairs: the engine stamps
// Tier-6 on them and nothing else in the frame changes (the frames' index and column
// sets are invariant across finalizes of one prematch). So /finalize can return just
// those rows (api/serialize.py::frames_delta_to_json) instead of re-shipping ~96k —
// for Dubai that's ~75MB -> ~0.5MB, which is the difference between a 10s apply and
// an instant one.
//
// The delta is a pure function of the new result (the CURRENT Tier-6 row set), NOT a
// diff against what the client holds, so it cannot go stale.
//
// Patching must always start from the pristine `baseline` — the frames of the last
// FULL response — never from the already-patched displayed result. A patch only WRITES
// the delta's rows: a row absent from the delta keeps its baseline values. So removing a
// pair reverts correctly only if the baseline holds that pair UNSTAMPED. The baseline can
// hold stamps (the initial /reconcile applies the insurer's remembered pairs), and the
// client never received those rows' pre-match values — so removing a pair stamped in
// the baseline must fetch full frames instead (ManualMatchTab::baselineApplied), and
// every full response replaces the baseline.
//
// The prior-period passes run AFTER the hand matches, so a hand match can move their
// stamps too: the delta also carries every row they stamped (PRIOR_PERIOD_GIDS). A row
// they stamped in the baseline that the delta no longer carries has lost its stamp, and
// its unstamped values never reached the client, so that delta is refused (full frames).
// So is one whose run switched the run-wide Match Group labels on or off (labelled).
import type { FrameRecords, ReconResult } from "../types/api";
import { PRIOR_PERIOD_GIDS } from "./manualMatchLabels";

// Whether every row of `held` a prior-period pass stamped is also in `delta`.
function priorStampsCarried(held: FrameRecords, delta: FrameRecords): boolean {
  const gi = held.columns.indexOf("match_group_id");
  if (gi < 0) return true;
  const carried = new Set(delta.indices);
  return held.rows.every((row, i) => {
    const gid = String(row[gi] ?? "");
    return !PRIOR_PERIOD_GIDS.some((p) => gid.startsWith(p)) || carried.has(held.indices[i]);
  });
}

// Whether any row of the baseline carries a Match Group label. Labels are run-wide (one
// N:M match labels every plain pair "1:1"; releasing the last blanks them all), so a
// delta whose run differs here would leave every row it does not carry stale.
function labelled(...frames: FrameRecords[]): boolean {
  return frames.some((f) => {
    const li = f.columns.indexOf("match_group_label");
    return li >= 0 && f.rows.some((r) => String(r[li] ?? "") !== "");
  });
}

// Replace the delta's rows (by original pandas index) into a held frame. Returns null
// if the held frame doesn't line up with what the delta was built against — the caller
// then refetches full frames rather than render a frame it can't prove is correct.
//
// A row that dropped out of the Tier-6 set falls back to its BASELINE values — which
// are pre-match only if the baseline never stamped it (see the header for the rule
// that keeps that true).
function patchFrame(held: FrameRecords | undefined, delta: FrameRecords): FrameRecords | null {
  if (!held) return null;
  // total_rows on a delta carries the FULL frame length, so it validates the held copy.
  if (delta.total_rows !== held.rows.length) return null;
  if (delta.columns.length !== held.columns.length) return null;
  for (let i = 0; i < delta.columns.length; i++) {
    if (delta.columns[i] !== held.columns[i]) return null;
  }
  // Index values mirror the frame's pandas index (FrameRecords.indices), which is
  // nullable in the type even though the engine frames are always int-indexed.
  const pos = new Map<string | number | null, number>();
  for (let i = 0; i < held.indices.length; i++) pos.set(held.indices[i], i);
  const rows = held.rows.slice();
  for (let i = 0; i < delta.indices.length; i++) {
    const at = pos.get(delta.indices[i]);
    if (at === undefined) return null; // delta row the held frame doesn't have
    rows[at] = delta.rows[i];
  }
  return { columns: held.columns, rows, indices: held.indices, total_rows: delta.total_rows };
}

// Fold a delta response into a complete ReconResult using `baseline` for the unchanged
// rows. Every non-frame figure (summary, track_summary, group_breakdown, …) comes from
// the response itself — the server recomputes those in full, so nothing is stale.
// Returns null when the delta can't be applied safely; callers refetch full frames.
export function applyFramesDelta(resp: ReconResult, baseline: ReconResult): ReconResult | null {
  const dc = resp.df_cri_delta;
  const di = resp.df_insurer_delta;
  if (!dc || !di) return null; // not a delta response — caller uses it as-is
  if (!baseline.df_cri || !baseline.df_insurer) return null;
  if (!priorStampsCarried(baseline.df_cri, dc) || !priorStampsCarried(baseline.df_insurer, di)) {
    return null;
  }
  if (resp.match_groups_labelled !== undefined
      && resp.match_groups_labelled !== labelled(baseline.df_cri, baseline.df_insurer)) {
    return null;
  }
  const cri = patchFrame(baseline.df_cri, dc);
  const ins = patchFrame(baseline.df_insurer, di);
  if (!cri || !ins) return null;
  const out: ReconResult = { ...resp, df_cri: cri, df_insurer: ins };
  delete out.df_cri_delta;
  delete out.df_insurer_delta;
  delete out.match_groups_labelled;
  return out;
}
